import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiErrorMessage } from "../api/client";
import { roleLabel } from "../lib/roleLabel";
import { formatWhen } from "../lib/when";

/**
 * An agent's settings — when it runs, whether it runs, who it answers to and
 * who signs its work.
 *
 * All of this was declared in code and visible on the roster, and none of it
 * could be changed: a workspace could read that the pack goes at 06:30 and
 * had to ask an engineer to move it. The schedule is edited as a frequency
 * and a time rather than as a crontab, because nobody should type
 * `30 6 * * *` to move a report half an hour, and a free-text cron field is a
 * way of shipping a syntax error into a scheduler.
 */

const DAYS = [
  { v: 0, label: "Sunday" }, { v: 1, label: "Monday" }, { v: 2, label: "Tuesday" },
  { v: 3, label: "Wednesday" }, { v: 4, label: "Thursday" }, { v: 5, label: "Friday" },
  { v: 6, label: "Saturday" },
];

const APPROVERS = ["cxo", "reviewer", "operator", "agent_handler"] as const;

export interface AgentScheduleView {
  kind: string | null;
  enabled: boolean;
  frequency: "daily" | "weekly" | "monthly" | null;
  day_of_week: number | null;
  day_of_month: number | null;
  hour: number | null;
  minute: number | null;
  tz: string | null;
  next_run_at: string | null;
  owner_person_id: string | null;
}

interface Person {
  id: string;
  name: string;
  title: string | null;
}

export function AgentSettings({
  workspaceId,
  handKey,
  handName,
  schedule,
  decisions,
  canConfigure,
  canSetApprover,
}: {
  workspaceId: string;
  handKey: string;
  handName: string;
  schedule: AgentScheduleView | null;
  decisions: { key: string; label: string; approver_role: string }[];
  canConfigure: boolean;
  canSetApprover: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const people = useQuery<{ people: Person[] }, Error>({
    queryKey: ["org-people", workspaceId],
    enabled: open && !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/org/people`),
  });

  const [frequency, setFrequency] = useState(schedule?.frequency ?? "daily");
  const [dow, setDow] = useState(schedule?.day_of_week ?? 0);
  const [dom, setDom] = useState(schedule?.day_of_month ?? 1);
  const [time, setTime] = useState(
    `${String(schedule?.hour ?? 7).padStart(2, "0")}:${String(schedule?.minute ?? 0).padStart(2, "0")}`,
  );
  const [owner, setOwner] = useState(schedule?.owner_person_id ?? "");

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPatch(`/api/workspaces/${workspaceId}/workforce/${handKey}/schedule`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workforce", workspaceId] }),
  });

  const setApprover = useMutation({
    mutationFn: (args: { decisionKey: string; role: string }) =>
      apiPatch(
        `/api/workspaces/${workspaceId}/autonomy/${handKey}/${args.decisionKey}/approver`,
        { approver_role: args.role },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["workforce", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["autonomy", workspaceId] });
    },
  });

  if (!canConfigure && !canSetApprover) return null;

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
      <button
        type="button"
        className="btn"
        data-testid={`configure-${handKey}`}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Close settings" : "Settings"}
      </button>

      {open ? (
        <div className="card-section" data-testid={`agent-settings-${handKey}`}>
          {canConfigure ? (
            <>
              <div className="section-eyebrow">WHEN IT RUNS</div>
              {schedule?.kind === "event" ? (
                <div className="play-meta">
                  <span>
                    {handName} runs on an event, not a clock. There is no time
                    to set — it runs when a record arrives.
                  </span>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <select
                    aria-label={`Frequency for ${handName}`}
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value as typeof frequency)}
                  >
                    <option value="daily">Every day</option>
                    <option value="weekly">Every week</option>
                    <option value="monthly">Every month</option>
                  </select>
                  {frequency === "weekly" ? (
                    <select
                      aria-label={`Day for ${handName}`}
                      value={dow}
                      onChange={(e) => setDow(Number(e.target.value))}
                    >
                      {DAYS.map((d) => (
                        <option key={d.v} value={d.v}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {frequency === "monthly" ? (
                    <select
                      aria-label={`Day of the month for ${handName}`}
                      value={dom}
                      onChange={(e) => setDom(Number(e.target.value))}
                    >
                      {/* Capped at 28: not every month has a 29th, and a
                          report that skips February is a report nobody
                          trusts. */}
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <label style={{ color: "var(--text-3)" }}>
                    at{" "}
                    <input
                      type="time"
                      aria-label={`Time for ${handName}`}
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                    />
                  </label>
                  <span style={{ color: "var(--text-3)" }}>
                    {schedule?.tz ?? "Asia/Dubai"}
                  </span>
                </div>
              )}

              <div className="section-eyebrow" style={{ marginTop: 12 }}>
                WHO IT ANSWERS TO
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                <select
                  aria-label={`Owner for ${handName}`}
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)} style={{ minWidth: 220 }}
                >
                  <option value="">Nobody named yet</option>
                  {(people.data?.people ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.title ? ` — ${p.title}` : ""}
                    </option>
                  ))}
                </select>
                <span style={{ color: "var(--text-3)" }}>
                  From the organisation directory in Settings.
                </span>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn primary"
                  data-testid={`save-schedule-${handKey}`}
                  disabled={save.isPending}
                  onClick={() => {
                    const [h, m] = time.split(":");
                    save.mutate({
                      ...(schedule?.kind === "cron"
                        ? {
                            frequency,
                            day_of_week: dow,
                            day_of_month: dom,
                            hour: Number(h),
                            minute: Number(m),
                          }
                        : {}),
                      owner_person_id: owner || null,
                    });
                  }}
                >
                  {save.isPending ? "Saving…" : "Save settings"}
                </button>
                <button
                  type="button"
                  className="btn"
                  data-testid={`toggle-enabled-${handKey}`}
                  disabled={save.isPending}
                  onClick={() => save.mutate({ enabled: !(schedule?.enabled ?? true) })}
                >
                  {schedule?.enabled === false ? "Switch the agent on" : "Switch the agent off"}
                </button>
                {schedule?.next_run_at ? (
                  <span style={{ color: "var(--text-3)" }}>
                    Next run {formatWhen(schedule.next_run_at)}
                  </span>
                ) : null}
                {save.isError ? (
                  <span className="pill red">{apiErrorMessage(save.error)}</span>
                ) : null}
                {save.isSuccess && !save.isPending ? (
                  <span style={{ color: "var(--accent)" }}>Saved.</span>
                ) : null}
              </div>
            </>
          ) : null}

          {canSetApprover && decisions.length > 0 ? (
            <>
              <div className="section-eyebrow" style={{ marginTop: 12 }}>
                WHO SIGNS ITS WORK
              </div>
              {decisions.map((d) => (
                <div
                  key={d.key}
                  style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}
                >
                  <span>{d.label}</span>
                  <select
                    aria-label={`Approver for ${d.label}`}
                    value={d.approver_role}
                    onChange={(e) => setApprover.mutate({ decisionKey: d.key, role: e.target.value })}
                  >
                    {APPROVERS.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
              {setApprover.isError ? (
                <span className="pill red">
                  {apiErrorMessage(setApprover.error)}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
